import { isAfter, isWithinInterval, subDays } from "date-fns";
import { TODO } from "@revelate/types";
import { buildRiskHistoryDealSnapshot } from "../lib/risk-history";
import {
  evaluateRiskIndicators,
  getRevelateExpectedCloseDate,
  getTotalDealRiskScoreInCategory,
  getWeightedDealRiskScoreInCategory,
} from "../lib/risk";
import {
  categoryIndicators,
  mapRiskName,
  type RiskIndicator,
} from "../lib/risk-contracts";
import {
  getPipelineLabel,
  getStageLabel,
  getStagePipelineId,
  getStageProbability,
} from "../lib/pipelines";
import {
  getPipelineRiskSeverity,
  type PipelineRiskSeverity,
  type StateOfSalesV2PipelineRiskDeal,
} from "./pipelineAnalysis";

const comparisonDays = 30;
const riskChangeThreshold = 0.5;
const pipelineConcentrationThresholds = [
  0.05,
  0.10,
  0.15,
  0.20,
  0.25,
  0.30,
  0.40,
  0.50,
  0.60,
  0.70,
  0.80,
  0.90,
];

type ForecastContributionDeal = {
  forecast_contribution?: number | null;
  id: number | string;
};

export type PipelineRiskMovementIndicatorChange = {
  currentRisk: number;
  indicator: RiskIndicator;
  label: string;
  previousRisk: number;
};

export type PipelineRiskMovementDeal = StateOfSalesV2PipelineRiskDeal & {
  changedIndicators: PipelineRiskMovementIndicatorChange[];
  currentDealValue: number;
  currentRisk: number;
  currentSeverity: PipelineRiskSeverity;
  previousDealValue: number;
  previousRisk: number;
  previousSeverity: PipelineRiskSeverity;
  riskChange: number;
};

export type PipelineRiskMovementCategory = {
  count: number;
  dealValue: number;
  deals: PipelineRiskMovementDeal[];
};

export type PipelineRiskMovement = {
  asOf: string;
  available: boolean;
  comparisonDays: number;
  currentHighRiskDealValue: number;
  deals: PipelineRiskMovementDeal[];
  eligibleDealCount: number;
  enteredHighRisk: PipelineRiskMovementCategory;
  excludedDealCount: number;
  highRiskValueChanged: PipelineRiskMovementCategory;
  leftHighRisk: PipelineRiskMovementCategory;
  netHighRiskDealValueChange: number;
  previousHighRiskDealValue: number;
  riskIncreased: PipelineRiskMovementCategory;
  riskReduced: PipelineRiskMovementCategory;
  totalCurrentDealCount: number;
  unchangedDealCount: number;
};

function finiteNumber(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number : 0;
}

function hasHistoryAt(
  deal: TODO,
  property: string,
  asOf: Date,
) {
  const history = deal.provider_data?.propertiesWithHistory?.[property];
  if (!Array.isArray(history)) return false;
  return history.some((record) => {
    if (!record?.timestamp) return false;
    const timestamp = new Date(record.timestamp);
    return !Number.isNaN(timestamp.getTime()) && !isAfter(timestamp, asOf);
  });
}

function getCreatedDate(deal: TODO) {
  const rawDate = deal.provider_data?.properties?.createdate
    ?? deal.provider_data?.properties?.hs_createdate
    ?? deal.created_at;
  if (!rawDate) return null;
  const date = new Date(rawDate);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getHistoricalNormalizedDealValue(
  deal: TODO,
  historicalProperties: Record<string, unknown>,
  asOf: Date,
) {
  const currentNormalizedValue = finiteNumber(deal.value);
  if (!hasHistoryAt(deal, "amount", asOf)) return currentNormalizedValue;

  const historicalAmount = Number(historicalProperties.amount);
  if (!Number.isFinite(historicalAmount)) return currentNormalizedValue;
  const currentRawValue = finiteNumber(
    deal.valueInDealCurrency ?? deal.value,
  );
  const conversionFactor = currentRawValue !== 0
    ? currentNormalizedValue / currentRawValue
    : 1;
  return historicalAmount * conversionFactor;
}

function pruneEmailsAt(deal: TODO, asOf: Date) {
  if (!Array.isArray(deal.emails)) return [];
  return deal.emails
    .filter((email: TODO) => {
      if (!email?.timestamp) return false;
      const timestamp = new Date(email.timestamp);
      return !Number.isNaN(timestamp.getTime()) && !isAfter(timestamp, asOf);
    })
    .toSorted((a: TODO, b: TODO) => (
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      || String(a.id ?? "").localeCompare(String(b.id ?? ""))
    ));
}

function keepTimestampKnownAt(
  deal: TODO,
  historicalProperties: Record<string, unknown>,
  property: string,
  asOf: Date,
) {
  if (hasHistoryAt(deal, property, asOf)) return;
  const value = historicalProperties[property];
  if (!value) {
    delete historicalProperties[property];
    return;
  }
  const timestamp = new Date(String(value));
  if (Number.isNaN(timestamp.getTime()) || isAfter(timestamp, asOf)) {
    delete historicalProperties[property];
  }
}

function isExpectedCloseInPeriod(
  value: Date | string | null | undefined,
  period: { from: Date; to: Date },
) {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  return isWithinInterval(date, {
    start: period.from,
    end: period.to,
  });
}

function addPipelineConcentration(
  deals: TODO[],
) {
  const totalOpenDealValue = deals.reduce(
    (sum, deal) => sum + finiteNumber(deal.value),
    0,
  );
  if (totalOpenDealValue <= 0 || deals.length === 0) return deals;

  const sortedDeals = deals.toSorted(
    (a, b) => finiteNumber(b.value) - finiteNumber(a.value),
  );
  const concentrationValue = totalOpenDealValue * 0.5;

  for (const threshold of pipelineConcentrationThresholds) {
    const dealCount = Math.ceil(deals.length * threshold);
    const concentratedDeals = sortedDeals.slice(0, dealCount);
    const concentratedValue = concentratedDeals.reduce(
      (sum, deal) => sum + finiteNumber(deal.value),
      0,
    );
    if (concentratedValue <= concentrationValue) continue;

    const concentratedIds = new Set(concentratedDeals.map(deal => String(deal.id)));
    return deals.map(deal => (
      concentratedIds.has(String(deal.id))
        ? { ...deal, pipelineConcentration: threshold * 100 }
        : deal
    ));
  }

  return deals;
}

function evaluatePipelineRiskCohort({
  asOf,
  companyId,
  deals,
  dealstageAvgTimes,
  pipelines,
}: {
  asOf: Date;
  companyId: number | null | undefined;
  deals: TODO[];
  dealstageAvgTimes: TODO[] | undefined;
  pipelines: TODO[];
}) {
  const prepared = deals.map((deal) => {
    const probabilityValue = deal.provider_data?.properties
      ?.hs_deal_stage_probability_shadow;
    const probability = Number(probabilityValue);
    const hasProbability = Boolean(probabilityValue) && Number.isFinite(probability);
    return {
      ...structuredClone(deal),
      deal_scale: 10,
      emails: pruneEmailsAt(deal, asOf),
      percentage_of_total_value: 10,
      risks: {},
      stage_scale: hasProbability ? probability.toFixed(2) : 0,
    };
  });
  const concentrated = addPipelineConcentration(prepared);

  return evaluateRiskIndicators(
    concentrated,
    companyId,
    pipelines,
    dealstageAvgTimes,
    asOf,
  ).map(deal => ({
    ...deal,
    risk_score: getTotalDealRiskScoreInCategory(deal, "forecast"),
    weighted_risk_score: getWeightedDealRiskScoreInCategory(deal, "forecast"),
    pipeline_risk_score: getWeightedDealRiskScoreInCategory(deal, "pipelineRisk"),
  }));
}

function buildHistoricalDeal(
  deal: TODO,
  pipelines: TODO[],
  dealstageAvgTimes: TODO[] | undefined,
  asOf: Date,
) {
  const createdDate = getCreatedDate(deal);
  if (!createdDate || isAfter(createdDate, asOf)) return null;
  if (
    !hasHistoryAt(deal, "dealstage", asOf)
    || !hasHistoryAt(deal, "closedate", asOf)
  ) {
    return null;
  }

  const snapshot = buildRiskHistoryDealSnapshot(deal, asOf);
  const properties = snapshot.provider_data?.properties ?? {};
  const stageId = String(properties.dealstage ?? "");
  const pipelineId = String(
    hasHistoryAt(deal, "pipeline", asOf)
      ? properties.pipeline
      : getStagePipelineId(pipelines, stageId) ?? properties.pipeline ?? deal.pipeline ?? "",
  );
  const pipeline = pipelines.find(item => String(item.id) === pipelineId);
  if (!pipeline?.stages?.some((stage: TODO) => String(stage.id) === stageId)) {
    return null;
  }

  const stageProbability = getStageProbability(
    pipelines,
    pipelineId,
    stageId,
    true,
  );
  if (
    snapshot.is_closed
    || snapshot.is_won
    || snapshot.is_lost
    || stageProbability <= 0
    || stageProbability >= 1
  ) {
    return null;
  }

  const historicalProperties: Record<string, unknown> = {
    ...properties,
    dealstage: stageId,
    hs_deal_stage_probability_shadow: String(stageProbability),
    pipeline: pipelineId,
  };
  for (const property of [
    "notes_last_updated",
    "hs_lastmodifieddate",
    "hs_latest_sales_email_reply_date",
  ]) {
    keepTimestampKnownAt(deal, historicalProperties, property, asOf);
  }
  const historicalValue = getHistoricalNormalizedDealValue(
    deal,
    historicalProperties,
    asOf,
  );
  const historicalDeal = {
    ...snapshot,
    adjustedCloseDate: null,
    closed_at: historicalProperties.closedate,
    emails: pruneEmailsAt(deal, asOf),
    pipeline: pipelineId,
    pipeline_label: getPipelineLabel(pipelines, pipelineId),
    stage_label: getStageLabel(pipelines, pipelineId, stageId),
    stage_probability: getStageProbability(pipelines, pipelineId, stageId),
    stage_probability_adjusted: stageProbability,
    provider_data: {
      ...snapshot.provider_data,
      properties: historicalProperties,
    },
    value: historicalValue,
  };
  const adjustedCloseDate = getRevelateExpectedCloseDate(
    historicalDeal,
    pipelines,
    dealstageAvgTimes,
    asOf,
  );

  return {
    ...historicalDeal,
    adjustedCloseDate,
  };
}

function buildChangedIndicators(
  previousDeal: TODO,
  currentDeal: TODO,
) {
  return categoryIndicators.pipelineRisk.flatMap((indicator) => {
    const previousRisk = finiteNumber(previousDeal.risks?.[indicator]?.risk);
    const currentRisk = finiteNumber(currentDeal.risks?.[indicator]?.risk);
    if (Math.abs(currentRisk - previousRisk) < riskChangeThreshold) return [];
    return [{
      currentRisk,
      indicator,
      label: mapRiskName(indicator),
      previousRisk,
    }];
  });
}

function category(
  deals: PipelineRiskMovementDeal[],
  getValue: (deal: PipelineRiskMovementDeal) => number = deal => finiteNumber(deal.value),
): PipelineRiskMovementCategory {
  return {
    count: deals.length,
    dealValue: deals.reduce((sum, deal) => sum + getValue(deal), 0),
    deals: deals.toSorted((a, b) => (
      Math.abs(b.riskChange) - Math.abs(a.riskChange)
      || finiteNumber(b.value) - finiteNumber(a.value)
      || String(a.id).localeCompare(String(b.id))
    )),
  };
}

export function buildPipelineRiskMovement({
  companyId,
  currentDeals,
  dealstageAvgTimes,
  evaluationDate = new Date(),
  forecastDeals,
  pipelines,
  selectedPeriodRange,
}: {
  companyId: number | null | undefined;
  currentDeals: TODO[];
  dealstageAvgTimes: TODO[] | undefined;
  evaluationDate?: Date;
  forecastDeals: ForecastContributionDeal[];
  pipelines: TODO[];
  selectedPeriodRange: { from: Date; to: Date };
}): PipelineRiskMovement {
  const asOf = subDays(evaluationDate, comparisonDays);
  const forecastById = new Map(
    forecastDeals.map(deal => [
      String(deal.id),
      finiteNumber(deal.forecast_contribution),
    ]),
  );
  const uniqueCurrentDeals = Array.from(
    new Map(currentDeals.map(deal => [String(deal.id), deal])).values(),
  );
  const historicalCandidates = uniqueCurrentDeals.map(deal => ({
    current: deal,
    historical: buildHistoricalDeal(
      deal,
      pipelines,
      dealstageAvgTimes,
      asOf,
    ),
  }));
  const eligiblePairs = historicalCandidates.filter(
    (pair): pair is { current: TODO; historical: TODO } => (
      pair.historical !== null
      && isExpectedCloseInPeriod(pair.historical.adjustedCloseDate, selectedPeriodRange)
      && isExpectedCloseInPeriod(pair.current.adjustedCloseDate, selectedPeriodRange)
    ),
  );
  const currentEvaluated = evaluatePipelineRiskCohort({
    asOf: evaluationDate,
    companyId,
    deals: eligiblePairs.map(pair => pair.current),
    dealstageAvgTimes,
    pipelines,
  });
  const previousEvaluated = evaluatePipelineRiskCohort({
    asOf,
    companyId,
    deals: eligiblePairs.map(pair => pair.historical),
    dealstageAvgTimes,
    pipelines,
  });
  const previousById = new Map(
    previousEvaluated.map(deal => [String(deal.id), deal]),
  );
  const movementDeals = currentEvaluated.flatMap((currentDeal) => {
    const previousDeal = previousById.get(String(currentDeal.id));
    if (!previousDeal) return [];
    const currentRisk = finiteNumber(currentDeal.pipeline_risk_score);
    const previousRisk = finiteNumber(previousDeal.pipeline_risk_score);
    const currentDealValue = finiteNumber(currentDeal.value);
    const previousDealValue = finiteNumber(previousDeal.value);
    return [{
      ...currentDeal,
      changedIndicators: buildChangedIndicators(previousDeal, currentDeal),
      currentDealValue,
      currentRisk,
      currentSeverity: getPipelineRiskSeverity(currentRisk),
      forecast_contribution: forecastById.get(String(currentDeal.id)) ?? 0,
      previousDealValue,
      previousRisk,
      previousSeverity: getPipelineRiskSeverity(previousRisk),
      riskChange: currentRisk - previousRisk,
    } as PipelineRiskMovementDeal];
  });
  const enteredHighRiskDeals = movementDeals.filter(
    deal => deal.previousSeverity !== "high" && deal.currentSeverity === "high",
  );
  const leftHighRiskDeals = movementDeals.filter(
    deal => deal.previousSeverity === "high" && deal.currentSeverity !== "high",
  );
  const riskIncreasedDeals = movementDeals.filter(
    deal => deal.previousSeverity === deal.currentSeverity
      && deal.riskChange >= riskChangeThreshold,
  );
  const riskReducedDeals = movementDeals.filter(
    deal => deal.previousSeverity === deal.currentSeverity
      && deal.riskChange <= -riskChangeThreshold,
  );
  const highRiskValueChangedDeals = movementDeals.filter(
    deal => deal.previousSeverity === "high"
      && deal.currentSeverity === "high"
      && Math.abs(deal.currentDealValue - deal.previousDealValue) >= 0.5,
  );
  const previousHighRiskDealValue = movementDeals
    .filter(deal => deal.previousSeverity === "high")
    .reduce((sum, deal) => sum + deal.previousDealValue, 0);
  const currentHighRiskDealValue = movementDeals
    .filter(deal => deal.currentSeverity === "high")
    .reduce((sum, deal) => sum + deal.currentDealValue, 0);

  return {
    asOf: asOf.toISOString(),
    available: movementDeals.length > 0,
    comparisonDays,
    currentHighRiskDealValue,
    deals: movementDeals,
    eligibleDealCount: movementDeals.length,
    enteredHighRisk: category(enteredHighRiskDeals, deal => deal.currentDealValue),
    excludedDealCount: uniqueCurrentDeals.length - movementDeals.length,
    highRiskValueChanged: category(
      highRiskValueChangedDeals,
      deal => deal.currentDealValue - deal.previousDealValue,
    ),
    leftHighRisk: category(leftHighRiskDeals, deal => deal.previousDealValue),
    netHighRiskDealValueChange: currentHighRiskDealValue - previousHighRiskDealValue,
    previousHighRiskDealValue,
    riskIncreased: category(riskIncreasedDeals),
    riskReduced: category(riskReducedDeals),
    totalCurrentDealCount: uniqueCurrentDeals.length,
    unchangedDealCount: movementDeals.filter(
      deal => deal.previousSeverity === deal.currentSeverity
        && Math.abs(deal.riskChange) < riskChangeThreshold
        && Math.abs(deal.currentDealValue - deal.previousDealValue) < 0.5,
    ).length,
  };
}
